import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

const CATEGORIES = ["Workmanship", "Delay / scheduling", "Communication", "Billing / payment", "Staff conduct", "Other"];

export default function Complaints() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", postcode: "", reference: "", category: "Workmanship", message: "" });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/complaint", form);
      setSent(true);
      toast.success("Complaint received — we've emailed you a confirmation.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again or call us.");
    }
    setSubmitting(false);
  };

  return (
    <main data-testid="complaints-page" className="px-6 md:px-12 lg:px-16 pt-16 pb-24">
      <div className="label-eyebrow mb-6">Complaints</div>
      <h1 className="font-black tracking-tighter leading-[0.95] text-5xl md:text-7xl max-w-3xl">Tell us if something's not right.</h1>
      <p className="mt-6 text-[#6B7280] max-w-2xl text-base leading-relaxed">We take every concern seriously. Submit the form below and it goes straight to our complaints team at <b>complaints@theextensionworks.co.uk</b>.</p>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          {sent ? (
            <div className="de-card p-12" data-testid="complaint-success">
              <div className="label-eyebrow mb-4 text-[#C79A3B]">Complaint logged</div>
              <p className="text-lg">Thank you. We've received your complaint and emailed you a confirmation. We aim to acknowledge within 3 working days and respond fully within 14 working days.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="de-card p-8 md:p-12 space-y-6" data-testid="complaint-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Name" value={form.name} onChange={v=>setForm({...form,name:v})} testid="complaint-name" required/>
                <Field label="Email" type="email" value={form.email} onChange={v=>setForm({...form,email:v})} testid="complaint-email" required/>
                <Field label="Phone" value={form.phone} onChange={v=>setForm({...form,phone:v})} testid="complaint-phone"/>
                <Field label="Postcode" value={form.postcode} onChange={v=>setForm({...form,postcode:v})} testid="complaint-postcode"/>
                <Field label="Booking reference (if any)" value={form.reference} onChange={v=>setForm({...form,reference:v})} testid="complaint-reference"/>
                <div>
                  <label className="label-eyebrow block mb-2">Category</label>
                  <select data-testid="complaint-category" value={form.category} onChange={e=>setForm({...form,category:e.target.value})}
                    className="w-full border border-[#E5E7EB] bg-white px-4 py-3 focus:outline-none focus:border-[#C79A3B]">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="label-eyebrow block mb-2">What happened? *</label>
                <textarea data-testid="complaint-message" required rows={7} value={form.message} onChange={e=>setForm({...form,message:e.target.value})}
                  className="w-full border border-[#E5E7EB] bg-white px-4 py-3 focus:outline-none focus:border-[#C79A3B]"/>
              </div>
              <button data-testid="complaint-submit" disabled={submitting} className="de-btn-primary" type="submit">{submitting ? "Submitting…" : "Submit complaint"}</button>
            </form>
          )}
        </div>

        <div className="de-card p-8 h-fit">
          <div className="label-eyebrow mb-3">Our complaints procedure</div>
          <ol className="text-sm leading-relaxed text-[#6B7280] space-y-3 list-decimal pl-4">
            <li>Submit this form (or email <b>complaints@theextensionworks.co.uk</b>).</li>
            <li>We acknowledge your complaint within <b>3 working days</b>.</li>
            <li>We investigate and respond fully within <b>14 working days</b>.</li>
            <li>If you're still unhappy, we'll escalate to a director for a final review.</li>
          </ol>
          <div className="de-hr my-6" />
          <div className="text-sm leading-relaxed">
            <span className="label-eyebrow block mb-1">Prefer to call?</span>
            0203 488 1081 · Mon–Fri 08:00–18:00
          </div>
        </div>
      </div>
    </main>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}{required && " *"}</label>
      <input data-testid={testid} type={type} required={required} value={value}
        onChange={e=>onChange(e.target.value)}
        className="w-full border border-[#E5E7EB] bg-white px-4 py-3 focus:outline-none focus:border-[#C79A3B]"/>
    </div>
  );
}
